import { create } from 'zustand';
import { ADMIN_ROLES } from '@/shared/constants/permissions';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  username: string | null;
  role: string | null;
  roles: string[];
  permissions: string[];
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasPermission: (perm: string) => boolean;
  login: (token: string, username: string, role: string, roles: string[], permissions: string[], refreshToken?: string) => void;
  logout: () => void;
}

function loadRoles(): string[] {
  try {
    return JSON.parse(localStorage.getItem('auth_roles') || '[]');
  } catch {
    return [];
  }
}

function loadPermissions(): string[] {
  try {
    return JSON.parse(localStorage.getItem('auth_permissions') || '[]');
  } catch {
    return [];
  }
}

function checkIsAdmin(roles: string[]): boolean {
  return roles.some((r) => (ADMIN_ROLES as readonly string[]).includes(r));
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: localStorage.getItem('auth_token'),
  refreshToken: localStorage.getItem('auth_refresh_token'),
  username: localStorage.getItem('auth_username'),
  role: localStorage.getItem('auth_role'),
  roles: loadRoles(),
  permissions: loadPermissions(),
  isAuthenticated: !!localStorage.getItem('auth_token'),
  isAdmin: checkIsAdmin(loadRoles()),

  hasPermission: (perm: string) => {
    return get().permissions.includes(perm);
  },

  login: (token: string, username: string, role: string, roles: string[], permissions: string[], refreshToken?: string) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_username', username);
    localStorage.setItem('auth_role', role);
    localStorage.setItem('auth_roles', JSON.stringify(roles));
    localStorage.setItem('auth_permissions', JSON.stringify(permissions));
    if (refreshToken) {
      localStorage.setItem('auth_refresh_token', refreshToken);
    }
    set({
      token, username, role, roles, permissions,
      refreshToken: refreshToken || get().refreshToken,
      isAuthenticated: true,
      isAdmin: checkIsAdmin(roles),
    });
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_username');
    localStorage.removeItem('auth_role');
    localStorage.removeItem('auth_roles');
    localStorage.removeItem('auth_permissions');
    set({
      token: null, refreshToken: null, username: null, role: null,
      roles: [], permissions: [],
      isAuthenticated: false, isAdmin: false,
    });
  },
}));
